import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Switch } from "./ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { SuccessModal } from "./SuccessModal";
import { useLanguage } from "./LanguageContext";
import { db, supabase } from "../lib/supabase";
import { EyeOffIcon } from "lucide-react";

// Simple toast function instead of sonner
const showToast = (message: string) => {
  // Create a simple toast notification
  const toast = document.createElement("div");
  toast.className =
    "fixed top-4 right-4 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-lg z-50 transition-all duration-300";
  toast.textContent = message;
  document.body.appendChild(toast);

  setTimeout(() => {
    toast.style.transform = "translateX(100%)";
    setTimeout(() => {
      document.body.removeChild(toast);
    }, 300);
  }, 3000);
};

// Icons
const DownloadIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="7,10 12,15 17,10"></polyline>
    <line x1="12" y1="15" x2="12" y2="3"></line>
  </svg>
);

const EditIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const CameraIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"></path>
    <circle cx="12" cy="13" r="3"></circle>
  </svg>
);

const LockIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
    <circle cx="12" cy="16" r="1"></circle>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
  </svg>
);

const ShieldIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
  </svg>
);

const EyeIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);

const FileIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"></path>
  </svg>
);

interface ProfilePageProps {
  userType: "investor";
}

export function ProfilePage({}: /*  userType */ ProfilePageProps) {
  const { /* t, */ getBilingualText } = useLanguage();
  const [activeTab, setActiveTab] = useState("personal");
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [changePhotoOpen, setChangePhotoOpen] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // Password validation
  const passwordRequirements = [
    { met: passwordForm.newPassword.length >= 8, label: getBilingualText("profile.security.minChars") },
    { met: /[0-9]/.test(passwordForm.newPassword), label: getBilingualText("profile.security.oneNumber") },
  ];

  const isPasswordValid = passwordRequirements.every(r => r.met) &&
    passwordForm.newPassword === passwordForm.confirmPassword &&
    passwordForm.newPassword !== passwordForm.currentPassword;

  const handlePasswordChange = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showToast(String(getBilingualText("profile.security.passwordsNotMatch")));
      return;
    }

    setPasswordLoading(true);
    try {
      const result = await db.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      showToast(String(getBilingualText("profile.security.success")));
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      
      // If all sessions were terminated, user needs to log in again
      if (result.allSessionsTerminated) {
        // The auth state change listener will handle redirecting to login
        await supabase.auth.signOut();
      }
    } catch (error: any) {
      showToast(String(getBilingualText("profile.security.error")));
    } finally {
      setPasswordLoading(false);
    }
  };


  // const [twoFactorOpen, setTwoFactorOpen] = useState(false);
  // const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);

  // Success modal states
  const [successModal, setSuccessModal] = useState({
    isOpen: false,
    title: "",
    message: "",
  });

  // User data (will be populated from DB when available)
  const [userData, setUserData] = useState({
    name: "Inversor Demo",
    email: "socio@bellamysignature.com",
    phone: "+34 612 345 678",
    address: "Madrid, España",
    investmentGoal: "Crecimiento a largo plazo",
    riskProfile: "Moderado",
    monthlyContribution: "2500",
    memberSince: "Enero 2023",
    invested: 430750,
    investedFormatted: "€430.750",
  });

  // Which fields actually come from the DB — used to hide non-DB fallbacks
  const [dbAvailable, setDbAvailable] = useState({
    name: false,
    email: false,
    phone: false,
    address: false,
    investmentGoal: false,
    monthlyContribution: false,
    invested: false,
    memberSince: false,
  });
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [documentsList, setDocumentsList] = useState<any[]>([]);

  const [preferences, setPreferences] = useState({
    language: "es",
    currency: "eur",
    timezone: "Europe/Madrid",
    reportFrequency: "monthly",
  });

  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    smsAlerts: false,
    pushNotifications: true,
    periodicReports: true,
    marketNews: false,
    portfolioAlerts: true,
    transactionAlerts: true,
  });

  // Mock documents fallback; real documents loaded into `documentsList`
  const documents = [
    { id: 1, nombre: "Contrato de Inversión Principal", tipo: "Contrato", fecha: "15/01/2023", estado: "Firmado", tamaño: "2.4 MB" },
    { id: 2, nombre: "KYC - Conoce a tu Cliente", tipo: "Verificación", fecha: "12/01/2023", estado: "Aprobado", tamaño: "1.8 MB" },
    { id: 3, nombre: "Reporte Trimestral Q4 2023", tipo: "Reporte", fecha: "31/12/2023", estado: "Disponible", tamaño: "5.2 MB" },
    { id: 4, nombre: "Certificado de Participación", tipo: "Certificado", fecha: "20/01/2023", estado: "Emitido", tamaño: "890 KB" },
    { id: 5, nombre: "Política de Privacidad", tipo: "Legal", fecha: "01/01/2023", estado: "Vigente", tamaño: "650 KB" },
  ];

  const displayDocuments = documentsList.length ? documentsList : documents;

  // Load user/profile/docs from DB on mount
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoadingProfile(true);
      try {
        const res = await supabase.auth.getUser();
        const uid = (res?.data as any)?.user?.id;
        setCurrentUserId(uid ?? null);
        if (!uid) return;

        const u = await db.getUser(uid).catch(() => null);
        const profile = await db.getInvestorProfile(uid).catch(() => null);
        const reports = await db.getReports(uid).catch(() => []);
        const portfolio = await db.getPortfolio(uid).catch(() => null);
        // contributions are stored with investor_id referencing investor_profiles.id
        const contributions = await db
          .getContributionsByInvestor(profile?.id ?? uid)
          .catch(() => []);

        if (!mounted) return;
        if (u) {
          const name = `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();
          const memberSince = (() => {
            try {
              const d = new Date(u.created_at);
              return d.toLocaleString("es-ES", { month: "long", year: "numeric" });
            } catch (e) {
              return null;
            }
          })();

          setUserData((prev) => ({
            ...prev,
            name: name || prev.name,
            email: u.email ?? prev.email,
            phone: (u as any).phone ?? prev.phone,
            memberSince: memberSince ?? prev.memberSince,
            address: (u as any).address ?? prev.address,
          }));

          setDbAvailable((prev) => ({
            ...prev,
            name: Boolean(name),
            email: Boolean(u.email),
            phone: Boolean((u as any).phone),
            address: Boolean((u as any).address),
            memberSince: Boolean(memberSince),
          }));
        }
        if (profile) {
          setUserData((prev) => ({
            ...prev,
            investmentGoal: (profile.investment_goals && profile.investment_goals.length) ? profile.investment_goals.join(', ') : prev.investmentGoal,
            riskProfile: (profile as any).risk_tolerance ?? prev.riskProfile,
          }));
        }
        // Map portfolio/contributions into profile card
        // Compute 'invested' strictly from contributions (no fallback)
        try {
          const contribSum = (contributions && Array.isArray(contributions))
            ? contributions.reduce((acc: number, c: any) => acc + Number(c.amount ?? 0), 0)
            : 0;

          const invested = contribSum || 0;
          const investedFormatted = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(invested);
          setUserData((prev) => ({ ...prev, invested, investedFormatted }));
          setDbAvailable((prev) => ({ ...prev, invested: contribSum > 0, monthlyContribution: contribSum > 0 }));

          if (contributions && Array.isArray(contributions) && contributions.length > 0) {
            const sorted = [...contributions].sort((a: any, b: any) => new Date(b.contribution_date).getTime() - new Date(a.contribution_date).getTime());
            const latest = sorted[0];
            if (latest) {
              setUserData((prev) => ({ ...prev, monthlyContribution: String(latest.amount ?? prev.monthlyContribution) }));
            }
          }
        } catch (e) {
          // ignore errors computing sums
        }
        setDocumentsList(reports || []);
      } catch (e) {
        console.error('Error loading profile:', e);
      } finally {
        setLoadingProfile(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {getBilingualText("profile.page.title")}
          </h1>
          <p className="text-gray-600 mt-1">
            {getBilingualText("profile.page.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            size="sm"
            className="bg-gray-900 hover:bg-gray-800 text-white"
            onClick={() => showToast("Modo de edición activado")}
          >
            <EditIcon />
            {getBilingualText("profile.actions.editProfile")}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Left Column - Profile Card */}
        <div className="lg:col-span-1">
          <div className="bg-[#fafafa] rounded-lg border border-transparent shadow-lg p-6 text-gray-900">
            {loadingProfile ? (
              <div className="animate-pulse">
                <div className="text-center mb-6">
                  <div className="relative inline-block">
                    <div className="w-20 h-20 bg-gray-300 rounded-full mb-4 mx-auto" />
                  </div>
                  <div className="h-4 bg-gray-300 rounded w-3/4 mx-auto mb-2" />
                  <div className="h-3 bg-gray-300 rounded w-1/2 mx-auto mb-4" />
                  <div className="h-3 bg-gray-300 rounded w-1/3 mx-auto" />
                </div>

                <div className="space-y-3 mb-6 px-4">
                  <div className="h-3 bg-gray-300 rounded w-full" />
                  <div className="h-3 bg-gray-300 rounded w-5/6" />
                  <div className="h-3 bg-gray-300 rounded w-2/3" />
                </div>
              </div>
            ) : (
              <>
                {/* Avatar */}
                <div className="text-center mb-6">
                  <div className="relative inline-block">
                    <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center text-2xl font-semibold text-gray-600 mb-4">
                      ID
                    </div>
                  </div>

                  <h3 className="font-semibold text-gray-900 mb-1">{userData.name}</h3>
                  <p className="text-sm text-gray-600 mb-3">{userData.email}</p>

                  <Badge className="bg-green-100 text-green-800 border-green-200 mb-4">
                    🏆 {getBilingualText("profile.certifiedInvestor")}
                  </Badge>
                </div>

                {/* Stats */}
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">{getBilingualText("profile.memberSince")}</span>
                    <span className="font-medium text-gray-900">{userData.memberSince}</span>
                  </div>

                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">{getBilingualText("profile.invested")}</span>
                    <span className="font-medium text-gray-900">{userData.investedFormatted}</span>
                  </div>
                </div>
              </>
            )}

            {/* Change Photo Button - removed per request (hidden) */}
            
          </div>
        </div>

        {/* Right Column - Content Tabs */}
        <div className="lg:col-span-3">
          <div className="bg-[#fafafa] rounded-lg border border-transparent shadow-lg overflow-hidden text-gray-900">
            {loadingProfile ? (
              <div className="p-6">
                <div className="animate-pulse space-y-4">
                  <div className="h-6 bg-gray-300 rounded w-1/3" />
                  <div className="h-4 bg-gray-300 rounded w-2/3" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="h-10 bg-gray-300 rounded" />
                    <div className="h-10 bg-gray-300 rounded" />
                    <div className="h-10 bg-gray-300 rounded" />
                    <div className="h-10 bg-gray-300 rounded" />
                  </div>
                </div>
              </div>
            ) : (
              <Tabs
                value={activeTab}
                onValueChange={setActiveTab}
                className="w-full"
              >
              <TabsList className="grid w-full grid-cols-5 bg-gray-50 rounded-none p-1 h-auto gap-1">
                <TabsTrigger
                  value="personal"
                  className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm py-3 px-4 text-sm font-medium"
                >
                  {getBilingualText("profile.tabs.personal")}
                </TabsTrigger>
                {/* <TabsTrigger 
                  value="preferences"
                  className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm py-3 px-4 text-sm font-medium"
                >
                  {getBilingualText("profile.tabs.preferences")}
                </TabsTrigger>
                <TabsTrigger 
                  value="notifications"
                  className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm py-3 px-4 text-sm font-medium"
                >
                  {getBilingualText("profile.tabs.notifications")}
                </TabsTrigger>
                <TabsTrigger 
                  value="documentacion"
                  className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm py-3 px-4 text-sm font-medium"
                >
                  {getBilingualText("profile.tabs.documentation")}
                </TabsTrigger> */}
                <TabsTrigger
                  value="security"
                  className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm py-3 px-4 text-sm font-medium"
                >
                  {getBilingualText("profile.tabs.security")}
                </TabsTrigger>
              </TabsList>

              {/* Personal Tab */}
              <TabsContent value="personal" className="p-6 mt-0">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      {getBilingualText("profile.personal.title")}
                    </h3>
                    <p className="text-gray-600 mb-6">
                      {getBilingualText("profile.personal.subtitle")}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label
                        htmlFor="name"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        {getBilingualText("profile.personal.fullName")}
                      </Label>
                      <Input
                        id="name"
                        value={userData.name}
                        onChange={(e) =>
                          setUserData({ ...userData, name: e.target.value })
                        }
                        className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm w-full"
                      />
                    </div>

                    {dbAvailable.email && (
                      <div>
                        <Label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          {getBilingualText("profile.personal.email")}
                        </Label>

                        <div className="relative">
                          <Input
                            id="email"
                            value={userData.email}
                            className="mt-2 h-10 rounded-full bg-gray-50 border-0 px-4 shadow-sm w-full pr-10 text-gray-600 cursor-not-allowed"
                            readOnly
                            aria-readonly={true}
                            tabIndex={-1}
                            title={String(getBilingualText('profile.personal.emailReadOnlyNote') || 'El correo no puede editarse desde aquí.')}
                          />

                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 flex items-center gap-2 select-none">
                            <LockIcon />
                          </span>
                        </div>
                      </div>
                    )}

                    {dbAvailable.phone && (
                      <div>
                        <Label
                          htmlFor="phone"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          {getBilingualText("profile.personal.phone")}
                        </Label>
                        <Input
                          id="phone"
                          value={userData.phone}
                          onChange={(e) =>
                            setUserData({ ...userData, phone: e.target.value })
                          }
                          className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm w-full"
                        />
                      </div>
                    )}

                    {dbAvailable.address && (
                      <div>
                        <Label
                          htmlFor="address"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          {getBilingualText("profile.personal.address")}
                        </Label>
                        <Input
                          id="address"
                          value={userData.address}
                          onChange={(e) =>
                            setUserData({ ...userData, address: e.target.value })
                          }
                          className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm w-full"
                        />
                      </div>
                    )}
                  </div>

                  {dbAvailable.investmentGoal && (
                    <div>
                      <Label
                        htmlFor="goal"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        {getBilingualText("profile.personal.investmentGoals")}
                      </Label>
                      <Textarea
                        id="goal"
                        value={userData.investmentGoal}
                        onChange={(e) =>
                          setUserData({
                            ...userData,
                            investmentGoal: e.target.value,
                          })
                        }
                        className="w-full min-h-[80px] rounded-lg border-0 shadow-sm bg-white"
                      />
                    </div>
                  )}

                  <div className="pt-6">
                      <Button
                        variant="black"
                        disabled={saving}
                        onClick={async () => {
                          if (!currentUserId) {
                            showToast('Usuario no identificado');
                            return;
                          }

                          const fullName = (userData.name || '').trim();
                          if (!fullName) {
                            showToast(String(getBilingualText('profile.validation.nameRequired') || 'El nombre no puede estar vacío'));
                            return;
                          }

                          const parts = fullName.split(/\s+/);
                          const first_name = parts.length ? parts.shift()! : '';
                          const last_name = parts.length ? parts.join(' ') : '';

                          setSaving(true);
                          try {
                            await db.updateUser(currentUserId, { first_name, last_name });
                            setSuccessModal({ isOpen: true, title: String(getBilingualText('profile.success.personalSaved.title') || 'Cambios guardados'), message: String(getBilingualText('profile.success.personalSaved.message') || 'Tu información personal ha sido actualizada correctamente.') });
                          } catch (err) {
                            console.error('Error updating user name:', err);
                            showToast('Error actualizando nombre');
                          } finally {
                            setSaving(false);
                          }
                        }}
                        className=""
                      >
                        {saving ? (
                          <span className="inline-flex items-center gap-3">
                            <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                            </svg>
                            {getBilingualText('profile.saving') || 'Procesando…'}
                          </span>
                        ) : (
                          getBilingualText("profile.personal.saveChanges")
                        )}
                      </Button>
                    </div>
                </div>
              </TabsContent>

              {/* Preferences Tab */}
              <TabsContent value="preferences" className="p-6 mt-0">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Preferencias
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Configura tus preferencias de plataforma y visualización
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="block text-sm font-medium text-gray-700 mb-2">
                        Idioma
                      </Label>
                      <Select
                        value={preferences.language}
                        onValueChange={(value) =>
                          setPreferences({ ...preferences, language: value })
                        }
                      >
                        <SelectTrigger className="w-full mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="es">Español</SelectItem>
                          <SelectItem value="en">English</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="block text-sm font-medium text-gray-700 mb-2">
                        Moneda
                      </Label>
                      <Select
                        value={preferences.currency}
                        onValueChange={(value) =>
                          setPreferences({ ...preferences, currency: value })
                        }
                      >
                        <SelectTrigger className="w-full mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="eur">EUR - Euro</SelectItem>
                          <SelectItem value="usd">USD - Dólar</SelectItem>
                          <SelectItem value="gbp">GBP - Libra</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="block text-sm font-medium text-gray-700 mb-2">
                        Zona Horaria
                      </Label>
                      <Select
                        value={preferences.timezone}
                        onValueChange={(value) =>
                          setPreferences({ ...preferences, timezone: value })
                        }
                      >
                        <SelectTrigger className="w-full mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Europe/Madrid">
                            Madrid (UTC+1)
                          </SelectItem>
                          <SelectItem value="Europe/London">
                            Londres (UTC+0)
                          </SelectItem>
                          <SelectItem value="America/New_York">
                            Nueva York (UTC-5)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="block text-sm font-medium text-gray-700 mb-2">
                        Frecuencia de Reportes
                      </Label>
                      <Select
                        value={preferences.reportFrequency}
                        onValueChange={(value) =>
                          setPreferences({
                            ...preferences,
                            reportFrequency: value,
                          })
                        }
                      >
                        <SelectTrigger className="w-full mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="weekly">Semanal</SelectItem>
                          <SelectItem value="monthly">Mensual</SelectItem>
                          <SelectItem value="quarterly">Trimestral</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="pt-6">
                    <Button
                      onClick={() =>
                        setSuccessModal({
                          isOpen: true,
                          title: "Preferencias guardadas",
                          message:
                            "Tus preferencias han sido actualizadas correctamente.",
                        })
                      }
                      className="bg-gray-900 hover:bg-gray-800 text-white"
                    >
                      Guardar Cambios
                    </Button>
                  </div>
                </div>
              </TabsContent>

              {/* Notifications Tab */}
              <TabsContent value="notifications" className="p-6 mt-0">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Notificaciones
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Configura cómo y cuándo recibir notificaciones
                    </p>
                  </div>

                  <div className="space-y-6">
                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <div>
                        <h4 className="font-medium text-gray-900">
                          Notificaciones por Email
                        </h4>
                        <p className="text-sm text-gray-600">
                          Recibe actualizaciones por correo electrónico
                        </p>
                      </div>
                      <Switch
                        checked={notifications.emailNotifications}
                        onCheckedChange={(checked) =>
                          setNotifications({
                            ...notifications,
                            emailNotifications: checked,
                          })
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <div>
                        <h4 className="font-medium text-gray-900">
                          Alertas SMS
                        </h4>
                        <p className="text-sm text-gray-600">
                          Recibe alertas importantes por SMS
                        </p>
                      </div>
                      <Switch
                        checked={notifications.smsAlerts}
                        onCheckedChange={(checked) =>
                          setNotifications({
                            ...notifications,
                            smsAlerts: checked,
                          })
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <div>
                        <h4 className="font-medium text-gray-900">
                          Notificaciones Push
                        </h4>
                        <p className="text-sm text-gray-600">
                          Notificaciones en tiempo real en el navegador
                        </p>
                      </div>
                      <Switch
                        checked={notifications.pushNotifications}
                        onCheckedChange={(checked) =>
                          setNotifications({
                            ...notifications,
                            pushNotifications: checked,
                          })
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <div>
                        <h4 className="font-medium text-gray-900">
                          Reportes Periódicos
                        </h4>
                        <p className="text-sm text-gray-600">
                          Recibe reportes regulares de tu cartera
                        </p>
                      </div>
                      <Switch
                        checked={notifications.periodicReports}
                        onCheckedChange={(checked) =>
                          setNotifications({
                            ...notifications,
                            periodicReports: checked,
                          })
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <div>
                        <h4 className="font-medium text-gray-900">
                          Noticias de Mercado
                        </h4>
                        <p className="text-sm text-gray-600">
                          Alertas sobre noticias relevantes del mercado
                        </p>
                      </div>
                      <Switch
                        checked={notifications.marketNews}
                        onCheckedChange={(checked) =>
                          setNotifications({
                            ...notifications,
                            marketNews: checked,
                          })
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <div>
                        <h4 className="font-medium text-gray-900">
                          Alertas de Portfolio
                        </h4>
                        <p className="text-sm text-gray-600">
                          Notificaciones sobre cambios en tu cartera
                        </p>
                      </div>
                      <Switch
                        checked={notifications.portfolioAlerts}
                        onCheckedChange={(checked) =>
                          setNotifications({
                            ...notifications,
                            portfolioAlerts: checked,
                          })
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between py-3">
                      <div>
                        <h4 className="font-medium text-gray-900">
                          Alertas de Transacciones
                        </h4>
                        <p className="text-sm text-gray-600">
                          Confirmaciones de transacciones y movimientos
                        </p>
                      </div>
                      <Switch
                        checked={notifications.transactionAlerts}
                        onCheckedChange={(checked) =>
                          setNotifications({
                            ...notifications,
                            transactionAlerts: checked,
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="pt-6">
                    <Button
                      onClick={() =>
                        setSuccessModal({
                          isOpen: true,
                          title: "Notificaciones guardadas",
                          message:
                            "Tus preferencias de notificación han sido actualizadas.",
                        })
                      }
                      className="bg-gray-900 hover:bg-gray-800 text-white"
                    >
                      Guardar Cambios
                    </Button>
                  </div>
                </div>
              </TabsContent>

              {/* Documentation Tab */}
              <TabsContent value="documentacion" className="p-6 mt-0">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Documentación
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Accede a todos tus documentos y contratos
                    </p>
                  </div>

                  <div className="overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Documento</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Fecha</TableHead>
                          <TableHead>Estado</TableHead>
                          <TableHead>Tamaño</TableHead>
                          <TableHead>Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {displayDocuments.map((doc: any) => (
                          <TableRow key={doc.id}>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <FileIcon />
                                <span className="font-medium">
                                  {doc.nombre}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{doc.tipo}</Badge>
                            </TableCell>
                            <TableCell className="text-gray-600">
                              {doc.fecha}
                            </TableCell>
                            <TableCell>
                              <Badge
                                className={
                                  doc.estado === "Firmado" ||
                                  doc.estado === "Aprobado" ||
                                  doc.estado === "Emitido"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-blue-100 text-blue-800"
                                }
                              >
                                {doc.estado}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-gray-600">
                              {doc.tamaño}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    showToast(`Descargando ${doc.nombre}...`)
                                  }
                                >
                                  <DownloadIcon />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    showToast(`Abriendo ${doc.nombre}...`)
                                  }
                                >
                                  <EyeIcon />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </TabsContent>

              {/* Security Tab */}
              <TabsContent value="security" className="p-6 mt-0">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      {getBilingualText("profile.security.title")}
                    </h3>
                    <p className="text-gray-600 mb-6">
                      {getBilingualText("profile.security.subtitle")}
                    </p>
                  </div>

                  <div className="space-y-6">
                    {/* Change Password */}
                        <div className="p-6 rounded-lg border border-transparent shadow-lg bg-[#fafafa] text-gray-900">
                          <div className="flex items-start gap-4">
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <LockIcon />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900 mb-2">
                                {getBilingualText("profile.security.changePassword")}
                              </h4>
                              <p className="text-gray-600 mb-4">
                                {getBilingualText("profile.security.changePassword.desc")}
                              </p>

                              {/* Password Form */}
                              <div className="space-y-4">
                                {/* Current Password */}
                                <div>
                                  <Label className="block text-sm font-medium text-gray-700 mb-2">
                                    {getBilingualText("profile.security.currentPassword")}
                                  </Label>
                                  <div className="relative">
                                    <Input
                                      type={showPasswords.current ? "text" : "password"}
                                      value={passwordForm.currentPassword}
                                      onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                                      className="h-10 rounded-full bg-white border-0 px-4 shadow-sm pr-10"
                                    />
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                                      onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                                    >
                                      {showPasswords.current ? <EyeOffIcon /> : <EyeIcon />}
                                    </Button>
                                  </div>
                                </div>

                                {/* New Password */}
                                <div>
                                  <Label className="block text-sm font-medium text-gray-700 mb-2">
                                    {getBilingualText("profile.security.newPassword")}
                                  </Label>
                                  <div className="relative">
                                    <Input
                                      type={showPasswords.new ? "text" : "password"}
                                      value={passwordForm.newPassword}
                                      onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                                      className="h-10 rounded-full bg-white border-0 px-4 shadow-sm pr-10"
                                    />
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                                      onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                                    >
                                      {showPasswords.new ? <EyeOffIcon /> : <EyeIcon />}
                                    </Button>
                                  </div>

                                  {/* Password Requirements */}
                                  <div className="mt-2 space-y-1">
                                    {passwordRequirements.map((req, idx) => (
                                      <p key={idx} className={`text-xs ${req.met ? "text-green-600" : "text-gray-500"}`}>
                                        {req.met ? "✓" : "○"} {String(req.label)}
                                      </p>
                                    ))}
                                  </div>
                                </div>

                                {/* Confirm Password */}
                                <div>
                                  <Label className="block text-sm font-medium text-gray-700 mb-2">
                                    {getBilingualText("profile.security.confirmPassword")}
                                  </Label>
                                  <div className="relative">
                                    <Input
                                      type={showPasswords.confirm ? "text" : "password"}
                                      value={passwordForm.confirmPassword}
                                      onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                                      className={`h-10 rounded-full bg-white border-0 px-4 shadow-sm pr-10 ${passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword
                                          ? "ring-2 ring-red-300" : ""
                                        }`}
                                    />
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                                      onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                                    >
                                      {showPasswords.confirm ? <EyeOffIcon /> : <EyeIcon />}
                                    </Button>
                                  </div>
                                  {passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword && (
                                    <p className="text-xs text-red-600 mt-1">{getBilingualText("profile.security.passwordsNotMatch")}</p>
                                  )}
                                </div>

                                {/* Submit Button */}
                                <Button
                                  variant="black"
                                  onClick={handlePasswordChange}
                                  disabled={!passwordForm.currentPassword || !isPasswordValid || passwordLoading}
                                  className="w-full"
                                >
                                  {passwordLoading ? (
                                    <span className="inline-flex items-center gap-2">
                                      <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                      </svg>
                                      {getBilingualText("profile.security.updating")}
                                    </span>
                                  ) : (
                                    getBilingualText("profile.security.updatePassword")
                                  )}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>

                    {/* Two-Factor Authentication: ocultada por petición del usuario */}

                    {/* Sessions */}
                    {/* <div className="border border-gray-200 rounded-lg p-6">
                      <h4 className="font-medium text-gray-900 mb-4">
                        Sesiones Activas
                      </h4>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium">Navegador actual</p>
                            <p className="text-sm text-gray-600">
                              Chrome en Madrid, España
                            </p>
                          </div>
                          <Badge className="bg-green-100 text-green-800">
                            Actual
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                          <div>
                            <p className="font-medium">Móvil</p>
                            <p className="text-sm text-gray-600">
                              Safari en Madrid, España
                            </p>
                          </div>
                          <Button variant="outline" size="sm">
                            Cerrar sesión
                          </Button>
                        </div>
                      </div>
                    </div> */}

                    {/* Danger Zone */}
                    {/* <div className="border border-red-200 rounded-lg p-6 bg-red-50">
                      <h4 className="font-medium text-red-900 mb-2">
                        Zona de Peligro
                      </h4>
                      <p className="text-red-700 mb-4">
                        Estas acciones son permanentes e irreversibles
                      </p>
                      <Button
                        variant="destructive"
                        onClick={() => setDeleteAccountOpen(true)}
                      >
                        Eliminar Cuenta
                      </Button>
                    </div> */}
                  </div>
                </div>
              </TabsContent>
              </Tabs>
            )}
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={successModal.isOpen}
        onClose={() =>
          setSuccessModal({ isOpen: false, title: "", message: "" })
        }
        title={successModal.title}
        message={successModal.message}
      />
    </div>
  );
}
